import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { serviceAPI } from '../api/serviceAPI';
import { categoryLabel } from '../i18n/categoryNames';
import './ServiceEditPage.css';

interface ServicePackage {
  price: number;
  delivery_days: number;
  revisions: number;
  features: string[];
}

interface FAQ {
  question: string;
  answer: string;
}

const ServiceEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 폼 데이터
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  
  const [basicPackage, setBasicPackage] = useState<ServicePackage>({
    price: 50000,
    delivery_days: 3,
    revisions: 1,
    features: [],
  });
  
  const [standardPackage, setStandardPackage] = useState<ServicePackage>({
    price: 0,
    delivery_days: 5,
    revisions: 2,
    features: [],
  });
  
  const [premiumPackage, setPremiumPackage] = useState<ServicePackage>({
    price: 0,
    delivery_days: 7,
    revisions: 3,
    features: [],
  });
  
  const [portfolioImages, setPortfolioImages] = useState<string[]>([]);
  const [portfolioInput, setPortfolioInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [faqs, setFaqs] = useState<FAQ[]>([{ question: '', answer: '' }]);
  const [requirements, setRequirements] = useState('');
  const [sourceFilesIncluded, setSourceFilesIncluded] = useState(false);
  const [commercialUse, setCommercialUse] = useState(false);

  const [basicFeatureInput, setBasicFeatureInput] = useState('');
  const [standardFeatureInput, setStandardFeatureInput] = useState('');
  const [premiumFeatureInput, setPremiumFeatureInput] = useState('');

  const categories = serviceAPI.getCategories();

  const loadService = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await serviceAPI.getServiceById(parseInt(id));
      const service = response.data;
      
      setTitle(service.title);
      setCategory(getCategoryFromId(service.category_id));
      setDescription(service.description);
      
      setBasicPackage({
        price: service.price_basic,
        delivery_days: service.delivery_days_basic,
        revisions: service.revisions_basic || 0,
        features: parseJSON(service.service_includes),
      });
      
      if (service.price_standard) {
        setStandardPackage({
          price: service.price_standard,
          delivery_days: service.delivery_days_standard,
          revisions: service.revisions_standard || 0,
          features: [],
        });
      }
      
      if (service.price_premium) {
        setPremiumPackage({
          price: service.price_premium,
          delivery_days: service.delivery_days_premium,
          revisions: service.revisions_premium || 0,
          features: [],
        });
      }
      
      setPortfolioImages(parseJSON(service.portfolio_images));
      setTags(parseJSON(service.tags));
      setFaqs(parseJSON(service.faqs) || [{ question: '', answer: '' }]);
      setRequirements(service.requirements || '');
      setSourceFilesIncluded(service.source_files_included || false);
      setCommercialUse(service.commercial_use || false);

    } catch (err: any) {
      setError(err.response?.data?.message || t('svEdit.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadService();
  }, [loadService]);

  const parseJSON = (data: any): any => {
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return [];
      }
    }
    return [];
  };

  const getCategoryFromId = (categoryId: number): string => {
    const map: { [key: number]: string } = {
      1: 'design', 2: 'development', 3: 'marketing', 4: 'writing',
      5: 'video', 6: 'music', 7: 'business', 8: 'other',
    };
    return map[categoryId] || 'development';
  };

  // 기능 관리
  const addFeature = (type: 'basic' | 'standard' | 'premium') => {
    const input = type === 'basic' ? basicFeatureInput : type === 'standard' ? standardFeatureInput : premiumFeatureInput;
    if (!input.trim()) return;
    
    if (type === 'basic') {
      setBasicPackage({ ...basicPackage, features: [...basicPackage.features, input.trim()] });
      setBasicFeatureInput('');
    } else if (type === 'standard') {
      setStandardPackage({ ...standardPackage, features: [...standardPackage.features, input.trim()] });
      setStandardFeatureInput('');
    } else {
      setPremiumPackage({ ...premiumPackage, features: [...premiumPackage.features, input.trim()] });
      setPremiumFeatureInput('');
    }
  };

  const removeFeature = (type: 'basic' | 'standard' | 'premium', index: number) => {
    if (type === 'basic') {
      setBasicPackage({ ...basicPackage, features: basicPackage.features.filter((_, i) => i !== index) });
    } else if (type === 'standard') {
      setStandardPackage({ ...standardPackage, features: standardPackage.features.filter((_, i) => i !== index) });
    } else {
      setPremiumPackage({ ...premiumPackage, features: premiumPackage.features.filter((_, i) => i !== index) });
    }
  };

  // 포트폴리오 관리
  const addPortfolioImage = () => {
    if (portfolioInput.trim()) {
      setPortfolioImages([...portfolioImages, portfolioInput.trim()]);
      setPortfolioInput('');
    }
  };

  const removePortfolioImage = (index: number) => {
    setPortfolioImages(portfolioImages.filter((_, i) => i !== index));
  };

  // 태그 관리
  const addTag = () => {
    if (tagInput.trim() && tags.length < 10) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  // FAQ 관리
  const addFAQ = () => {
    setFaqs([...faqs, { question: '', answer: '' }]);
  };

  const removeFAQ = (index: number) => {
    if (faqs.length > 1) {
      setFaqs(faqs.filter((_, i) => i !== index));
    }
  };

  const updateFAQ = (index: number, field: 'question' | 'answer', value: string) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const handleSubmit = async () => {
    if (!id) return;

    try {
      setSaving(true);
      setError(null);

      const serviceData = {
        title: title.trim(),
        category,
        description: description.trim(),
        package_basic: basicPackage,
        package_standard: standardPackage.price > 0 ? standardPackage : undefined,
        package_premium: premiumPackage.price > 0 ? premiumPackage : undefined,
        portfolio_images: portfolioImages,
        tags: tags.filter(t => t.trim()),
        faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
        requirements: requirements.trim(),
        source_files_included: sourceFilesIncluded,
        commercial_use: commercialUse,
      };

      await serviceAPI.updateService(parseInt(id), serviceData);
      
      alert(t('svEdit.updated'));
      navigate('/freelancer-services');
      
    } catch (err: any) {
      setError(err.response?.data?.message || t('svEdit.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="service-edit-page">
        <div className="container">
          <div className="loading">{t('svEdit.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="service-edit-page">
        <div className="container">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/freelancer-services')}>{t('projectDetail.backToList')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="service-edit-page">
      <div className="container">
        <div className="page-header">
          <h1>✏️ {t('svEdit.title')}</h1>
          <p>{t('svEdit.subtitle')}</p>
        </div>

        <div className="form-container">
          {/* 기본 정보 */}
          <section className="form-section">
            <h2>{t('profile.basicInfo')}</h2>
            
            <div className="form-group">
              <label>{t('svCreate.svcTitle')}</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('svEdit.titlePlaceholder')}
                maxLength={100}
              />
              <small>{title.length}/100</small>
            </div>

            <div className="form-group">
              <label>{t('svCreate.categoryLabel')}</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {categoryLabel(cat.label)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('svCreate.descLabel')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('svCreate.descPlaceholder')}
                rows={8}
                maxLength={2000}
              />
              <small>{description.length}/2000</small>
            </div>
          </section>

          {/* 패키지 */}
          <section className="form-section">
            <h2>{t('svCreate.stepPackage')}</h2>
            
            {/* 베이직 */}
            <div className="package-card">
              <h3>💎 {t('svEdit.basicPkg')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('svCreate.price')}</label>
                  <input type="number" value={basicPackage.price} onChange={(e) => setBasicPackage({...basicPackage, price: parseInt(e.target.value) || 0})} min="5000" step="1000" />
                </div>
                <div className="form-group">
                  <label>{t('quoteModal.days')}</label>
                  <input type="number" value={basicPackage.delivery_days} onChange={(e) => setBasicPackage({...basicPackage, delivery_days: parseInt(e.target.value) || 1})} min="1" />
                </div>
                <div className="form-group">
                  <label>{t('quoteModal.revisionsLabel')}</label>
                  <input type="number" value={basicPackage.revisions} onChange={(e) => setBasicPackage({...basicPackage, revisions: parseInt(e.target.value) || 0})} min="0" />
                </div>
              </div>
              <div className="form-group">
                <label>{t('svCreate.features')}</label>
                <div className="feature-input">
                  <input type="text" value={basicFeatureInput} onChange={(e) => setBasicFeatureInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature('basic'))} placeholder={t('svCreate.featurePlaceholder')} />
                  <button type="button" onClick={() => addFeature('basic')}>{t('common.add')}</button>
                </div>
                <div className="feature-list">
                  {basicPackage.features.map((f, i) => (
                    <div key={i} className="feature-item">
                      <span>✓ {f}</span>
                      <button onClick={() => removeFeature('basic', i)}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 스탠다드 */}
            <div className="package-card">
              <h3>⭐ {t('svCreate.stdPkg')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('svCreate.price')}</label>
                  <input type="number" value={standardPackage.price} onChange={(e) => setStandardPackage({...standardPackage, price: parseInt(e.target.value) || 0})} min="0" step="1000" />
                </div>
                <div className="form-group">
                  <label>{t('quoteModal.days')}</label>
                  <input type="number" value={standardPackage.delivery_days} onChange={(e) => setStandardPackage({...standardPackage, delivery_days: parseInt(e.target.value) || 1})} min="1" />
                </div>
                <div className="form-group">
                  <label>{t('quoteModal.revisionsLabel')}</label>
                  <input type="number" value={standardPackage.revisions} onChange={(e) => setStandardPackage({...standardPackage, revisions: parseInt(e.target.value) || 0})} min="0" />
                </div>
              </div>
              <div className="form-group">
                <label>{t('svCreate.features')}</label>
                <div className="feature-input">
                  <input type="text" value={standardFeatureInput} onChange={(e) => setStandardFeatureInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature('standard'))} placeholder={t('svCreate.featurePlaceholder')} />
                  <button type="button" onClick={() => addFeature('standard')}>{t('common.add')}</button>
                </div>
                <div className="feature-list">
                  {standardPackage.features.map((f, i) => (
                    <div key={i} className="feature-item">
                      <span>✓ {f}</span>
                      <button onClick={() => removeFeature('standard', i)}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 프리미엄 */}
            <div className="package-card">
              <h3>👑 {t('svCreate.premPkg')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('svCreate.price')}</label>
                  <input type="number" value={premiumPackage.price} onChange={(e) => setPremiumPackage({...premiumPackage, price: parseInt(e.target.value) || 0})} min="0" step="1000" />
                </div>
                <div className="form-group">
                  <label>{t('quoteModal.days')}</label>
                  <input type="number" value={premiumPackage.delivery_days} onChange={(e) => setPremiumPackage({...premiumPackage, delivery_days: parseInt(e.target.value) || 1})} min="1" />
                </div>
                <div className="form-group">
                  <label>{t('quoteModal.revisionsLabel')}</label>
                  <input type="number" value={premiumPackage.revisions} onChange={(e) => setPremiumPackage({...premiumPackage, revisions: parseInt(e.target.value) || 0})} min="0" />
                </div>
              </div>
              <div className="form-group">
                <label>{t('svCreate.features')}</label>
                <div className="feature-input">
                  <input type="text" value={premiumFeatureInput} onChange={(e) => setPremiumFeatureInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature('premium'))} placeholder={t('svCreate.featurePlaceholder')} />
                  <button type="button" onClick={() => addFeature('premium')}>{t('common.add')}</button>
                </div>
                <div className="feature-list">
                  {premiumPackage.features.map((f, i) => (
                    <div key={i} className="feature-item">
                      <span>✓ {f}</span>
                      <button onClick={() => removeFeature('premium', i)}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 포트폴리오 */}
          <section className="form-section">
            <h2>{t('svEdit.portfolioImages')}</h2>
            <div className="form-group">
              <label>{t('svEdit.imageUrl')}</label>
              <div className="portfolio-input">
                <input type="text" value={portfolioInput} onChange={(e) => setPortfolioInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPortfolioImage())} placeholder={t('svCreate.portfolioPlaceholder')} />
                <button type="button" onClick={addPortfolioImage}>{t('common.add')}</button>
              </div>
              <div className="portfolio-list">
                {portfolioImages.map((url, i) => (
                  <div key={i} className="portfolio-item">
                    <img src={url} alt={`Portfolio ${i+1}`} onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150'; }} />
                    <button onClick={() => removePortfolioImage(i)}>×</button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 태그 */}
          <section className="form-section">
            <h2>{t('svCreate.stepTags')}</h2>
            <div className="form-group">
              <label>{t('svCreate.tagsLabel')}</label>
              <div className="tag-input">
                <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder={t('svCreate.tagPlaceholder')} disabled={tags.length >= 10} />
                <button type="button" onClick={addTag} disabled={tags.length >= 10}>{t('common.add')}</button>
              </div>
              <div className="tag-list">
                {tags.map((tag, i) => (
                  <span key={i} className="tag">
                    {tag}
                    <button onClick={() => removeTag(i)}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="form-section">
            <h2>{t('svEdit.faqTitle')}</h2>
            <div className="faq-section">
              {faqs.map((faq, i) => (
                <div key={i} className="faq-item">
                  <div className="faq-header">
                    <h4>FAQ {i + 1}</h4>
                    {faqs.length > 1 && (
                      <button type="button" onClick={() => removeFAQ(i)} className="btn-remove">{t('common.delete')}</button>
                    )}
                  </div>
                  <div className="form-group">
                    <label>{t('svCreate.question')}</label>
                    <input type="text" value={faq.question} onChange={(e) => updateFAQ(i, 'question', e.target.value)} placeholder={t('svEdit.qPlaceholder')} />
                  </div>
                  <div className="form-group">
                    <label>{t('svCreate.answer')}</label>
                    <textarea value={faq.answer} onChange={(e) => updateFAQ(i, 'answer', e.target.value)} placeholder={t('svCreate.aPlaceholder')} rows={3} />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addFAQ} className="btn-add-faq">+ {t('svCreate.addFaq')}</button>
            </div>
          </section>

          {/* 요구사항 */}
          <section className="form-section">
            <h2>{t('svEdit.reqTitle')}</h2>
            <div className="form-group">
              <label>{t('svCreate.reqLabel')}</label>
              <textarea value={requirements} onChange={(e) => setRequirements(e.target.value)} placeholder={t('svEdit.reqPlaceholder')} rows={6} />
            </div>
          </section>

          {/* 추가 옵션 */}
          <section className="form-section">
            <h2>{t('svCreate.stepOptions')}</h2>
            <div className="options-section">
              <div className="option-item">
                <label className="checkbox-label">
                  <input type="checkbox" checked={sourceFilesIncluded} onChange={(e) => setSourceFilesIncluded(e.target.checked)} />
                  <span className="checkbox-text">
                    <strong>{t('svCreate.sourceFiles')}</strong>
                    <small>{t('svEdit.sourceFilesDesc')}</small>
                  </span>
                </label>
              </div>
              <div className="option-item">
                <label className="checkbox-label">
                  <input type="checkbox" checked={commercialUse} onChange={(e) => setCommercialUse(e.target.checked)} />
                  <span className="checkbox-text">
                    <strong>{t('svCreate.commercial')}</strong>
                    <small>{t('svCreate.commercialDesc')}</small>
                  </span>
                </label>
              </div>
            </div>
          </section>

          {/* 제출 버튼 */}
          <div className="form-actions">
            <button className="btn-cancel" onClick={() => navigate('/freelancer-services')}>
              {t('common.cancel')}
            </button>
            <button className="btn-submit" onClick={handleSubmit} disabled={saving}>
              {saving ? t('svEdit.saving') : t('svEdit.submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceEditPage;
